import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { Copy01, RefreshCw01, Rocket02 } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { streamSse } from "@/lib/sse";
import { ApiError } from "@/lib/apiClient";
import { formatDateTime } from "@/lib/format";
import {
  generateGuid,
  generatePushIdentifier,
  useActivationAuthServers,
  usePaatProfiles,
  useValidateActivation,
  type ActivationProgress,
  type ActivationResultData,
} from "@/lib/testActivation";
import { Field, ProgressSteps, Row, TestHttpLogs, inputCls } from "./shared";
import { SectionHeader } from "@/components/application/section-headers/section-headers";
import { AppSelect } from "@/components/app/AppSelect";

interface FormState {
  authServerType: string;
  username: string;
  otp: string;
  password: string;
  activationGuid: string;
  pushIdentifier: string;
  applicationId: string;
  deviceType: string;
  paatProfileId: string;
  expiresInSeconds: string;
  tags: string;
}

/**
 * Per-konektor sada aktivačních polí. Legacy Thymeleaf zobrazovalo VŠECHNA pole
 * pro každý konektor; tady je zpřesňujeme dle toho, co konektor reálně
 * konzumuje (eCobra ≠ eChannels). Neznámý typ → široká sada (parita s legacy).
 */
interface ConnectorFields {
  pushIdentifier: boolean;
  applicationId: boolean;
  appIdRequired: boolean;
  deviceType: boolean;
  paatProfile: boolean;
}

function connectorFields(type: string): ConnectorFields {
  switch (type) {
    case "CSOB_ECHANNELS":
      return { pushIdentifier: true, applicationId: true, appIdRequired: false, deviceType: true, paatProfile: true };
    case "ECOBRA":
    case "ECOBRA_SOAP":
      return { pushIdentifier: true, applicationId: true, appIdRequired: true, deviceType: false, paatProfile: false };
    case "KB_CAAS":
      return { pushIdentifier: true, applicationId: false, appIdRequired: false, deviceType: false, paatProfile: true };
    case "PRIVAT":
      return { pushIdentifier: true, applicationId: false, appIdRequired: false, deviceType: false, paatProfile: false };
    default: // ETTS / neznámý — široká sada
      return { pushIdentifier: true, applicationId: true, appIdRequired: false, deviceType: true, paatProfile: true };
  }
}

export function TestActivationPage() {
  const { t } = useTranslation();
  const { options: authServers, isLoading: loadingAuth } = useActivationAuthServers();
  const paat = usePaatProfiles();

  const [form, setForm] = useState<FormState>({
    authServerType: "",
    username: "",
    otp: "",
    password: "",
    activationGuid: "",
    expiresInSeconds: "",
    tags: "",
    pushIdentifier: "",
    applicationId: "SmartToken",
    deviceType: "",
    paatProfileId: "",
  });
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }));

  const [steps, setSteps] = useState<ActivationProgress[]>([]);
  const [result, setResult] = useState<ActivationResultData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);

  // Vybraný auth server (default první dostupný).
  const authType = form.authServerType || authServers[0]?.value || "";
  const fields = connectorFields(authType);

  const start = async () => {
    if (!authType || !form.username || !form.otp || !form.password || !form.activationGuid) {
      setError(t("testing.validation.required"));
      return;
    }
    if (fields.applicationId && fields.appIdRequired && !form.applicationId.trim()) {
      setError(t("testing.validation.appIdRequired"));
      return;
    }
    setSteps([]);
    setResult(null);
    setError(null);
    setRunning(true);

    // Posíláme jen pole, která daný konektor konzumuje (přesnost vs legacy).
    const body = {
      authServerType: authType,
      username: form.username,
      otp: form.otp,
      password: form.password,
      activationGuid: form.activationGuid,
      pushIdentifier: fields.pushIdentifier ? form.pushIdentifier || null : null,
      applicationId: fields.applicationId ? form.applicationId || "SmartToken" : null,
      paatProfileId: fields.paatProfile ? form.paatProfileId || null : null,
      deviceType: fields.deviceType && form.deviceType ? parseInt(form.deviceType, 10) : null,
      // Volitelné a nezávislé na konektoru — token se dá nechat vypršet a označkovat,
      // aby po zátěžovém běhu šel dohledat a uklidit.
      expiresInSeconds: form.expiresInSeconds ? parseInt(form.expiresInSeconds, 10) : null,
      tags: form.tags
        ? form.tags.split(",").map((s) => s.trim()).filter(Boolean)
        : null,
    };

    try {
      await streamSse("/admin/api/v1/testing/activation/start", body, (ev) => {
        if (ev.event !== "progress" || !ev.data) return;
        const progress = JSON.parse(ev.data) as ActivationProgress;
        setSteps((s) => [...s, progress]);
        if (progress.status === "COMPLETED" && progress.data) setResult(progress.data);
        else if (progress.status === "FAILED") setError(progress.message);
      });
    } catch (e) {
      setError(e instanceof ApiError ? `HTTP ${e.status}` : (e as Error).message);
    } finally {
      setRunning(false);
    }
  };

  const reset = () => {
    setSteps([]);
    setResult(null);
    setError(null);
  };

  return (
    <div className="flex flex-col gap-6">
      <SectionHeader.Root>
        <SectionHeader.Group>
          <div className="flex-1">
            <SectionHeader.Heading>{t("testing.activation.title")}</SectionHeader.Heading>
            <SectionHeader.Subheading>{t("testing.activation.subtitle")}</SectionHeader.Subheading>
          </div>
        </SectionHeader.Group>
      </SectionHeader.Root>

      {/* Parametry */}
      <div className="flex flex-col gap-4 rounded-xl bg-primary p-5 ring-1 ring-secondary">
        <h2 className="text-md font-semibold text-primary">{t("testing.activation.params")}</h2>

        {/* Auth server výběr */}
        <Field label={`${t("testing.field.authServer")} *`}>
          {loadingAuth ? (
            <LoadingIndicator />
          ) : authServers.length === 0 ? (
            <p className="text-sm text-tertiary">{t("testing.activation.noAuthServers")}</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {authServers.map((a) => (
                <button
                  key={a.value}
                  type="button"
                  onClick={() => set("authServerType", a.value)}
                  className={`rounded-lg px-3 py-2 text-sm font-medium ring-1 ${
                    authType === a.value
                      ? "bg-brand-solid text-white ring-transparent"
                      : "bg-primary text-secondary ring-primary hover:bg-primary_hover"
                  }`}
                >
                  {a.label}
                </button>
              ))}
            </div>
          )}
        </Field>

        {/* Společné parametry */}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={`${t("testing.field.username")} *`}>
            <input className={inputCls} value={form.username} onChange={(e) => set("username", e.target.value)} />
          </Field>
          <Field label={`${t("testing.field.otp")} *`}>
            <input className={inputCls} value={form.otp} onChange={(e) => set("otp", e.target.value)} />
          </Field>
          <Field label={`${t("testing.field.password")} *`}>
            <input
              className={inputCls}
              type="password"
              autoComplete="off"
              value={form.password}
              onChange={(e) => set("password", e.target.value)}
            />
          </Field>
          <Field label={`${t("testing.field.activationGuid")} *`}>
            <div className="flex gap-2">
              <input className={inputCls} value={form.activationGuid} onChange={(e) => set("activationGuid", e.target.value)} />
              <Button size="md" color="secondary" iconLeading={RefreshCw01} onPress={() => set("activationGuid", generateGuid())}>
                {t("testing.generate")}
              </Button>
            </div>
          </Field>

          {/* Nezávislé na konektoru — token lze nechat vypršet a označkovat, aby po
              zátěžovém běhu šel dohledat a hromadně uklidit. */}
          <Field label={t("testing.field.expiresInSeconds")}>
            <input
              className={inputCls}
              type="number"
              min={1}
              placeholder={t("testing.field.expiresInSeconds.placeholder")}
              value={form.expiresInSeconds}
              onChange={(e) => set("expiresInSeconds", e.target.value)}
            />
          </Field>

          <Field label={t("testing.field.tags")}>
            <input
              className={inputCls}
              placeholder={t("testing.field.tags.placeholder")}
              value={form.tags}
              onChange={(e) => set("tags", e.target.value)}
            />
          </Field>
        </div>

        {/* Specifické pro konektor — pole dle toho, co daný konektor konzumuje. */}
        {authType && (
          <div className="grid gap-4 rounded-lg bg-secondary p-4 ring-1 ring-secondary sm:grid-cols-2">
            <div className="text-xs font-semibold tracking-wide text-tertiary uppercase sm:col-span-2">
              {t("testing.connectorSpecific")}
            </div>
            {fields.pushIdentifier && (
              <Field label={t("testing.field.pushIdentifier")}>
                <div className="flex gap-2">
                  <input className={inputCls} value={form.pushIdentifier} onChange={(e) => set("pushIdentifier", e.target.value)} />
                  <Button
                    size="md"
                    color="secondary"
                    iconLeading={RefreshCw01}
                    onPress={() => set("pushIdentifier", generatePushIdentifier())}
                  >
                    {t("testing.generate")}
                  </Button>
                </div>
              </Field>
            )}
            {fields.applicationId && (
              <Field label={fields.appIdRequired ? `${t("testing.field.applicationId")} *` : t("testing.field.applicationId")}>
                <input className={inputCls} value={form.applicationId} onChange={(e) => set("applicationId", e.target.value)} />
              </Field>
            )}
            {fields.deviceType && (
              <Field label={t("testing.field.deviceType")} hint={t("testing.field.deviceType.hint")}>
                <input
                  className={inputCls}
                  type="number"
                  min={1}
                  placeholder="34"
                  value={form.deviceType}
                  onChange={(e) => set("deviceType", e.target.value)}
                />
              </Field>
            )}
            {fields.paatProfile && (
              <Field label={t("testing.field.paatProfile")}>
                <AppSelect value={form.paatProfileId} onChange={(e) => set("paatProfileId", e.target.value)}>
                  <option value="">{t("testing.field.paatProfile.default")}</option>
                  {(paat.data ?? []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.id} — {p.profileName} ({p.platform})
                    </option>
                  ))}
                </AppSelect>
              </Field>
            )}
          </div>
        )}

        <div>
          <Button size="lg" iconLeading={Rocket02} isDisabled={running} onPress={() => void start()}>
            {running ? t("testing.running") : t("testing.activation.start")}
          </Button>
        </div>
      </div>

      <ProgressSteps steps={steps} />

      {/* Výsledek */}
      {result && (
        <div className="flex flex-col gap-3 rounded-xl bg-primary p-5 ring-1 ring-secondary">
          <h2 className="text-md font-semibold text-success-primary">✓ {t("testing.activation.success")}</h2>
          <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
            <Row label={t("tokens.col.tokenId")} value={result.tokenId} mono />
            <Row label={t("tokens.col.username")} value={result.username} />
            <Row label={t("tokens.field.activated")} value={formatDateTime(result.activatedAt)} />
            <Row label={t("testing.httpCalls")} value={String(result.httpLogs?.length ?? 0)} />
          </dl>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/tokens/$tokenId"
              params={{ tokenId: result.tokenId }}
              className="rounded-lg bg-brand-solid px-3 py-2 text-sm font-semibold text-white hover:bg-brand-solid_hover"
            >
              {t("testing.viewToken")}
            </Link>
            <Button size="md" color="secondary" iconLeading={RefreshCw01} onPress={reset}>
              {t("testing.runAnother")}
            </Button>
            <a
              href={`/admin/api/v1/testing/activation/export/${encodeURIComponent(result.tokenId)}?format=json`}
              target="_blank"
              rel="noreferrer"
              className="rounded-lg px-3 py-2 text-sm font-medium text-secondary ring-1 ring-primary hover:bg-primary_hover"
            >
              {t("testing.exportJson")}
            </a>
            <a
              href={`/admin/api/v1/testing/activation/export/${encodeURIComponent(result.tokenId)}?format=csv`}
              target="_blank"
              rel="noreferrer"
              className="rounded-lg px-3 py-2 text-sm font-medium text-secondary ring-1 ring-primary hover:bg-primary_hover"
            >
              {t("testing.exportCsv")}
            </a>
          </div>
        </div>
      )}

      {error && !result && (
        <div className="rounded-xl bg-primary p-5 ring-1 ring-error">
          <h2 className="text-md font-semibold text-error-primary">✗ {t("testing.activation.failed")}</h2>
          <p className="mt-1 text-sm text-secondary">{error}</p>
        </div>
      )}

      {result && <TestHttpLogs logs={result.httpLogs ?? []} />}

      {/* Validate (bezpodmínečně — parita s Thymeleaf) */}
      <ValidateSection defaultUser={form.username} defaultOtp={form.otp} defaultGuid={form.activationGuid} />
    </div>
  );
}

function ValidateSection({
  defaultUser,
  defaultOtp,
  defaultGuid,
}: {
  defaultUser: string;
  defaultOtp: string;
  defaultGuid: string;
}) {
  const { t } = useTranslation();
  const validate = useValidateActivation();
  const [userName, setUserName] = useState("");
  const [otp, setOtp] = useState("");
  const [guid, setGuid] = useState("");
  const [copied, setCopied] = useState(false);

  const res = validate.data;
  const run = async () => {
    if (!userName.trim() || !otp.trim()) return;
    setCopied(false);
    await validate.mutateAsync({ userName: userName.trim(), activationOtp: otp.trim(), activationGuid: guid.trim() || null });
  };
  const copyToken = () => {
    if (!res?.token) return;
    void navigator.clipboard.writeText(res.token).then(() => setCopied(true));
  };

  return (
    <div className="flex flex-col gap-4 rounded-xl bg-primary p-5 ring-1 ring-secondary">
      <h2 className="text-md font-semibold text-primary">{t("testing.validate.title")}</h2>
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label={t("testing.field.username")}>
          <input className={inputCls} value={userName} onChange={(e) => setUserName(e.target.value)} />
        </Field>
        <Field label={t("testing.field.otp")}>
          <input className={inputCls} value={otp} onChange={(e) => setOtp(e.target.value)} />
        </Field>
        <Field label={t("testing.field.guidOptional")}>
          <input className={inputCls} value={guid} onChange={(e) => setGuid(e.target.value)} />
        </Field>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="md" isDisabled={validate.isPending} onPress={() => void run()}>
          {validate.isPending ? t("testing.validate.running") : t("testing.validate.submit")}
        </Button>
        <Button
          size="md"
          color="secondary"
          onPress={() => {
            setUserName(defaultUser);
            setOtp(defaultOtp);
            setGuid(defaultGuid);
          }}
        >
          {t("testing.validate.copyFromMain")}
        </Button>
      </div>

      {res &&
        (res.success && res.token ? (
          <div className="flex flex-col gap-2 rounded-lg bg-secondary p-3 ring-1 ring-secondary">
            <span className="text-sm font-semibold text-success-primary">✓ {t("testing.validate.success")}</span>
            <textarea
              readOnly
              value={res.token}
              className="h-20 w-full rounded-md bg-primary p-2 font-mono text-xs text-secondary ring-1 ring-secondary"
            />
            <div className="flex items-center gap-3">
              <Button size="sm" color="secondary" iconLeading={Copy01} onPress={copyToken}>
                {copied ? t("apikeys.copy") + " ✓" : t("testing.validate.copyToken")}
              </Button>
              {res.durationMs != null && (
                <span className="text-xs text-tertiary">
                  {res.durationMs} ms{res.testTraceId ? ` · ${res.testTraceId}` : ""}
                </span>
              )}
            </div>
          </div>
        ) : (
          <div className="rounded-lg bg-secondary p-3 ring-1 ring-error">
            <span className="text-sm font-semibold text-error-primary">✗ {t("testing.validate.failed")}</span>
            <p className="mt-1 text-sm text-secondary">{res.message || res.error || t("common.loadError")}</p>
          </div>
        ))}
    </div>
  );
}
